import React, { useState } from "react";
import { injectIntl } from "react-intl";
import { Button } from "antd";
import { CheckOutlined, CopyOutlined, LogoutOutlined, MailOutlined } from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import "./NoAccess.less";

const SUPPORT_EMAIL = "support@2way.app";

// Signed in (Auth0) but not linked to a 2way account, or the account gives this login no access:
// a full screen that says who is signed in and the three ways in — the team adds you (your
// address ready to copy and send), switch to the address your team uses, or ask support.
const NoAccess = ({intl}) => {
    const {user, logout} = useAuth0() || {};
    const t = (id, defaultMessage, values) => intl.formatMessage({id, defaultMessage}, values);
    const email = user?.email || "";
    const [copied, setCopied] = useState(false);

    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t('noAccess.mailSubject', 'Access to 2way'))}`
        + (email ? `&body=${encodeURIComponent(t('noAccess.mailBody', 'I am signed in as {email} and have no access to 2way.', {email}))}` : '');

    const signOut = () => {
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({type: 'loggedOut'}));
        }
        if (logout) logout({returnTo: window.origin});
    };

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(email);
        } catch (e) {
            const field = document.createElement('textarea');
            field.value = email;
            document.body.appendChild(field);
            field.select();
            document.execCommand('copy');
            field.remove();
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const initial = (user?.name || email || '?').trim().charAt(0).toUpperCase();

    return (
        <main className="no-access">
            <div className="no-access__inner">
                <section className="no-access__intro" aria-labelledby="no-access-title">
                    <img className="no-access__logo no-access__logo--light" src="/logo.svg" alt="2way"/>
                    <img className="no-access__logo no-access__logo--dark" src="/logo-dark.svg" alt="2way"/>
                    <p className="no-access__kicker">{t('noAccess.kicker', 'No access yet')}</p>
                    <h1 id="no-access-title" className="no-access__title">
                        {t('noAccess.title', 'This login isn\'t part of a 2way account.')}
                    </h1>
                    {email ? (
                        <div className="no-access__who">
                            <span className="no-access__avatar" aria-hidden="true">{initial}</span>
                            <span className="no-access__who-text">
                                <span className="no-access__who-label">{t('noAccess.signedInAs', 'Signed in as')}</span>
                                <strong>{email}</strong>
                            </span>
                            <button type="button" className="no-access__switch" onClick={signOut}>
                                {t('noAccess.notYou', 'Not you?')}
                            </button>
                        </div>
                    ) : null}
                    <p className="no-access__text">
                        {t('noAccess.why', 'Either nobody has added this address to an account yet, or the account it belongs to doesn\'t give it access.')}
                    </p>
                </section>

                <section className="no-access__ways" aria-labelledby="no-access-ways">
                    <h2 id="no-access-ways" className="no-access__ways-title">{t('noAccess.waysTitle', 'How to get in')}</h2>
                    <ol className="no-access__list">
                        <li className="no-access__way">
                            <span className="no-access__n">1</span>
                            <div className="no-access__way-body">
                                <h3>{t('noAccess.askTeamTitle', 'Ask your team to add you')}</h3>
                                <p>{t('noAccess.askTeamText', 'Whoever manages your company\'s 2way account can get this address added. Send it to them:')}</p>
                                {email ? (
                                    <div className="no-access__copy">
                                        <code>{email}</code>
                                        <Button size="small" icon={copied ? <CheckOutlined/> : <CopyOutlined/>} onClick={copyEmail}>
                                            {copied ? t('noAccess.copied', 'Copied') : t('noAccess.copy', 'Copy')}
                                        </Button>
                                    </div>
                                ) : null}
                            </div>
                        </li>
                        <li className="no-access__way">
                            <span className="no-access__n">2</span>
                            <div className="no-access__way-body">
                                <h3>{t('noAccess.switchTitle', 'Use the address your team uses')}</h3>
                                <p>{t('noAccess.switchText', 'Signed in with a private or old address? Sign out and sign in again with your work one.')}</p>
                                <Button icon={<LogoutOutlined/>} onClick={signOut}>{t('noAccess.signOutSwitch', 'Sign out and switch')}</Button>
                            </div>
                        </li>
                        <li className="no-access__way">
                            <span className="no-access__n">3</span>
                            <div className="no-access__way-body">
                                <h3>{t('noAccess.supportTitle', 'Still stuck? Ask us')}</h3>
                                <p>{t('noAccess.supportText', 'Write to support and we\'ll find your account. Your address is already in the message.')}</p>
                                <Button type="primary" icon={<MailOutlined/>} href={mailto}>{t('noAccess.contactSupport', 'Contact support')}</Button>
                            </div>
                        </li>
                    </ol>
                    <p className="no-access__support">
                        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                    </p>
                </section>
            </div>
        </main>
    );
};

export default injectIntl(NoAccess);
